import { ReactNode, useEffect, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { DataId } from "capitalrx-components-2.0/lib/general";
import { CustomReactSelectProps } from "../Dropdown/CustomReactSelectProps";
import { FieldBaseProps } from "../FieldBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { SubmitGroup } from "../SubmitGroup";
import { ValidityGroup } from "../ValidityGroup";
import { DisabledRequiredRule, required as requiredRule } from "../validation/rules";
import { DataDropdownDataSource } from "./DataDropdown";
import { Input } from "./Input";
import { OptionLabelProvider } from "./OptionLabelProvider";
import { AccessibilityProps } from "../../AccessibilityProps";

/**
 * The `Field` component properties.
 */
export interface FieldProps<TRow extends object> extends FieldBaseProps<DataId | null>, Pick<AccessibilityProps, "aria-label"> {
  /**
   * The data source to use to retrieve dropdown items.
   */
  readonly dataSource: DataDropdownDataSource<TRow>;

  /**
   * The function to call returning the label for a particular row.
   */
  readonly optionLabelProvider: OptionLabelProvider<TRow>;

  /**
   * The field to search against. If remote, corresponds to a field alias. If not, corresponds to a dot path to the field on the row.
   */
  readonly searchField?: string;

  /**
   * Props for search input
   */
  readonly searchInputProps?: CustomReactSelectProps["searchInputProps"];

  /**
   * Content to show after the text.
   */
  readonly prefix?: ReactNode;

  /**
   * Content to show prior to the text.
   */
  readonly suffix?: ReactNode;

  /**
   * The placeholder text.
   */
  readonly placeholder?: string;

  /**
   * Message to display when no options are returned from search.
   */
  readonly noOptionsMessage?: ReactNode;
}

/**
 * A React Hook Form compatible DataDropdown component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field = <TRow extends object>(props: FieldProps<TRow>) => {
  const { name, validator, required = false, defaultValue = null } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const rules = useMemo(
    () => ({
      ...(disabled || readOnly ? DisabledRequiredRule : requiredRule(required)),
      validate: disabled || readOnly ? undefined : validator,
    }),
    [required, validator, disabled, readOnly],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input
          {...props}
          ref={ref}
          value={value}
          onChange={onChange}
          onFocusExit={onBlur}
          clearable={!required}
          optionLabelProvider={props.optionLabelProvider as OptionLabelProvider<object>}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
